import { useState, useRef, useEffect } from 'react'
import { CircleCheck, AlertTriangle, CircleHelp } from 'lucide-react'
import { useMetaStatus } from '@/api/hooks/useMetaStatus'
import { useLanguage } from '@/i18n/LanguageContext'

// Badge de status da Graph API no topo. Serve pra distinguir "problema nosso"
// (token/restrição) de "outage da Meta" — quando o feed lista incidente, o
// operador vê na hora que a causa pode ser do lado da Meta.
export function MetaStatusBadge() {
  const { data } = useMetaStatus()
  const { t } = useLanguage()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [open])

  const status = data?.status ?? 'unknown'
  const isOutage = status === 'outage'
  const isOk = status === 'operational'

  const cfg = isOutage
    ? { icon: AlertTriangle, color: 'text-red-500', dot: 'bg-red-500', label: t('Meta: incidente') }
    : isOk
      ? { icon: CircleCheck, color: 'text-emerald-500', dot: 'bg-emerald-500', label: t('Meta OK') }
      : { icon: CircleHelp, color: 'text-muted-foreground', dot: 'bg-muted-foreground', label: t('Meta: ?') }

  const Icon = cfg.icon

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex h-8 items-center gap-1.5 rounded-lg border border-border px-2.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
        title={t('Status da Graph API (Meta)')}
      >
        <span className="relative flex h-2 w-2">
          {isOutage && (
            <span className={`absolute inline-flex h-full w-full animate-ping rounded-full ${cfg.dot} opacity-75`} />
          )}
          <span className={`relative inline-flex h-2 w-2 rounded-full ${cfg.dot}`} />
        </span>
        <Icon className={`h-3.5 w-3.5 ${cfg.color}`} />
      </button>

      {open && (
        <div className="absolute right-0 top-10 z-50 w-80 rounded-lg border border-border bg-background p-3 shadow-lg">
          <div className="mb-2 flex items-center gap-2 text-sm font-semibold">
            <Icon className={`h-4 w-4 ${cfg.color}`} />
            {cfg.label}
          </div>
          {isOk && (
            <p className="text-xs text-muted-foreground">
              {t('Nenhum incidente ativo na Graph API da Meta.')}
            </p>
          )}
          {status === 'unknown' && (
            <p className="text-xs text-muted-foreground">
              {t('Não foi possível consultar o status da Meta agora.')}
            </p>
          )}
          {isOutage && (
            <ul className="space-y-2">
              {data?.items?.slice(0, 5).map((item, i) => (
                <li key={item.guid || i} className="border-l-2 border-red-500 pl-2">
                  <a
                    href={item.link || 'https://metastatus.com/graph-api'}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block text-xs font-medium text-foreground hover:underline"
                  >
                    {item.title}
                  </a>
                  {item.pubDate && (
                    <span className="text-[10px] text-muted-foreground">{item.pubDate}</span>
                  )}
                  {item.description && (
                    <p className="mt-0.5 text-[11px] text-muted-foreground line-clamp-3">{item.description}</p>
                  )}
                </li>
              ))}
            </ul>
          )}
          <a
            href="https://metastatus.com/graph-api"
            target="_blank"
            rel="noopener noreferrer"
            className="mt-2 block text-[10px] text-muted-foreground hover:underline"
          >
            metastatus.com/graph-api →
          </a>
        </div>
      )}
    </div>
  )
}
